(() => {
  const parseJson = (raw, fallback) => {
    try {
      const data = JSON.parse(raw || '');
      return data ?? fallback;
    } catch {
      return fallback;
    }
  };

  const esc = (value) =>
    String(value ?? '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');

  const variantSize = (title = '') => {
    const t = String(title).toLowerCase();
    if (t.includes('150')) return '150g';
    if (t.includes('40')) return '40g';
    return '';
  };

  const lineSize = (item) => {
    const options = (item.options_with_values || []).map((opt) => opt.value).join(' ');
    const optionList = Array.isArray(item.options) ? item.options.join(' ') : '';
    return variantSize(
      `${item.variant_title || ''} ${item.sku || ''} ${item.product_title || ''} ${options} ${optionList}`
    );
  };

  const formatMoney = (cents, format) => {
    const amount = Number(cents) || 0;
    const placeholder = /\{\{\s*(\w+)\s*\}\}/;
    const formatString = format || 'AED {{amount}}';
    const withDelimiters = (number, precision = 2, thousands = ',', decimal = '.') => {
      const value = (Math.round(number) / 100).toFixed(precision);
      const parts = value.split('.');
      parts[0] = parts[0].replace(/(\d)(?=(\d{3})+(?!\d))/g, `$1${thousands}`);
      return parts[1] ? `${parts[0]}${decimal}${parts[1]}` : parts[0];
    };
    const token = formatString.match(placeholder)?.[1] || 'amount';
    let rendered = withDelimiters(amount, 2);
    if (token === 'amount_no_decimals') rendered = withDelimiters(amount, 0);
    if (token === 'amount_with_comma_separator') rendered = withDelimiters(amount, 2, '.', ',');
    if (token === 'amount_no_decimals_with_comma_separator') rendered = withDelimiters(amount, 0, '.', ',');
    return formatString.replace(placeholder, rendered);
  };

  const packsWord = (n) => (Number(n) === 1 ? 'pack' : 'packs');

  const cartForSize = (cart, size) =>
    (cart?.items || []).reduce(
      (acc, item) => {
        if (lineSize(item) !== size) return acc;
        acc.count += Number(item.quantity) || 0;
        acc.original += Number(item.original_line_price) || 0;
        return acc;
      },
      { count: 0, original: 0 }
    );

  const rewriteProductLinks = (pack) => {
    if (!pack) return;
    document.querySelectorAll('a[href*="/products/"]').forEach((link) => {
      try {
        const url = new URL(link.href, window.location.origin);
        if (!url.pathname.includes('/products/')) return;
        url.searchParams.set('pack', pack);
        link.href = url.toString();
      } catch {
        /* ignore */
      }
    });
  };

  const dealHref = (deal) => {
    const url = new URL(window.location.href);
    url.searchParams.set('pack', deal.size);
    url.searchParams.set('qty', String(deal.qty));
    return `${url.pathname}${url.search}`;
  };

  const fetchCart = async () => {
    try {
      const res = await fetch(`${window.Shopify?.routes?.root || '/'}cart.js`, {
        credentials: 'same-origin',
        headers: { Accept: 'application/json' },
      });
      if (!res.ok) return { items: [] };
      const data = await res.json();
      return data?.items ? data : { items: [] };
    } catch {
      return { items: [] };
    }
  };

  const notifyPack = () => {
    document.dispatchEvent(new CustomEvent('cm:packchange', { bubbles: true }));
  };

  const liveSizeAvailability = (root) => {
    const avail = {
      '40g': root.getAttribute('data-size-40') === 'true',
      '150g': root.getAttribute('data-size-150') === 'true',
    };

    document.querySelectorAll('[data-cm-card-sizes] .cm-card-size').forEach((btn) => {
      const size = variantSize(btn.getAttribute('data-title') || '');
      if (!size) return;
      if (btn.getAttribute('data-available') === 'true' && !btn.disabled) {
        avail[size] = true;
      }
    });

    return avail;
  };

  const withLiveAvailability = (deals, root) => {
    const avail = liveSizeAvailability(root);
    return (deals || []).map((deal) => ({
      ...deal,
      disabled: !avail[deal.size],
    }));
  };

  const render = (root, cart) => {
    const dealsEl = root.querySelector('[data-cm-builder-deals]');
    const statusEl = root.querySelector('[data-cm-builder-status]');
    if (!dealsEl || !statusEl) return;

    const deals = withLiveAvailability(parseJson(root.getAttribute('data-deals'), []), root);
    const moneyFormat = root.getAttribute('data-money-format');
    const params = new URLSearchParams(window.location.search);
    let pack = variantSize(params.get('pack') || '');
    let wantedQty = Number(params.get('qty')) || 0;
    const money = (cents) => formatMoney(cents, moneyFormat);
    const activeDeals = deals.filter((deal) => !deal.disabled);
    const avail = liveSizeAvailability(root);

    // Keep data attributes in sync for other listeners
    root.setAttribute('data-size-40', String(avail['40g']));
    root.setAttribute('data-size-150', String(avail['150g']));

    // Drop disabled pack from the URL (e.g. old 40g deep links)
    if (pack && !activeDeals.some((deal) => deal.size === pack)) {
      params.delete('pack');
      params.delete('qty');
      const next = `${window.location.pathname}${params.toString() ? `?${params}` : ''}${window.location.hash || ''}`;
      window.history.replaceState({}, '', next);
      pack = '';
      wantedQty = 0;
    }

    dealsEl.innerHTML = deals
      .map((deal) => {
        const qty = Number(deal.qty) || 1;
        const disabled = Boolean(deal.disabled);
        const active =
          !disabled && pack === deal.size && (wantedQty === qty || (!wantedQty && pack === deal.size));
        if (disabled) {
          return `<li>
            <span class="cm-builder__deal is-disabled" aria-disabled="true">
              <span class="cm-builder__deal-label">${esc(deal.label)}</span>
              <span class="cm-builder__deal-save">Out of stock</span>
            </span>
          </li>`;
        }
        return `<li>
          <a class="cm-builder__deal${active ? ' is-active' : ''}" href="${esc(dealHref(deal))}">
            <span class="cm-builder__deal-label">${esc(deal.label)}</span>
            <span class="cm-builder__deal-save">Save ${esc(deal.percent)}%</span>
          </a>
        </li>`;
      })
      .join('');

    if (!activeDeals.length) {
      statusEl.innerHTML = `<p class="cm-builder__hint">Bundle deals are unavailable while these pack sizes are out of stock.</p>`;
      return;
    }

    if (!pack) {
      statusEl.innerHTML = `<p class="cm-builder__hint">Tap a deal above, then add any matching flavors from the products below.</p>`;
      return;
    }

    const group = activeDeals.filter((deal) => deal.size === pack).sort((a, b) => a.qty - b.qty);
    const tallied = cartForSize(cart, pack);
    const unlocked = [...group].reverse().find((deal) => tallied.count >= Number(deal.qty));
    const goal =
      group.find((deal) => Number(deal.qty) === wantedQty && tallied.count < wantedQty) ||
      group.find((deal) => tallied.count < Number(deal.qty)) ||
      group[group.length - 1];
    const goalQty = Number(goal?.qty) || wantedQty || 1;
    const remaining = Math.max(0, goalQty - tallied.count);
    const pct = Number(unlocked?.percent) || 0;
    const save = pct ? Math.round((tallied.original * pct) / 100) : 0;
    const pay = Math.max(0, tallied.original - save);
    const bar = Math.max(0, Math.min(100, Math.round((tallied.count / goalQty) * 100)));

    statusEl.innerHTML = `
      <div class="cm-builder__meter">
        <div class="cm-builder__meter-top">
          <strong>${esc(unlocked ? `ON: ${unlocked.label}` : goal?.label || pack)}</strong>
          <span class="${unlocked ? 'cm-builder__on' : 'cm-builder__wait'}">${unlocked ? `Save ${unlocked.percent}%` : 'Not on yet'}</span>
        </div>
        <p class="cm-builder__count">${tallied.count} of ${goalQty} ${esc(pack)} ${packsWord(goalQty)}</p>
        <div class="cm-builder__bar" role="progressbar" aria-valuenow="${tallied.count}" aria-valuemin="0" aria-valuemax="${goalQty}">
          <span style="width:${bar}%"></span>
        </div>
        <div class="cm-builder__totals">
          <span>${remaining > 0 ? `Add ${remaining} more ${packsWord(remaining)} to turn on ${goal?.percent}% off.` : 'Best deal is on.'}</span>
          <strong>You pay ${esc(money(pay))}</strong>
        </div>
      </div>
    `;

    rewriteProductLinks(pack);
  };

  const bind = (root) => {
    if (root.dataset.cmBound === 'true') return;
    root.dataset.cmBound = 'true';

    let cart = { items: [] };
    let fetchTimer = 0;

    const paint = (nextCart) => {
      if (nextCart) cart = nextCart;
      render(root, cart);
    };

    const refresh = async () => {
      cart = await fetchCart();
      paint(cart);
    };

    const queueRefresh = (delay = 200) => {
      window.clearTimeout(fetchTimer);
      fetchTimer = window.setTimeout(refresh, delay);
    };

    root.addEventListener('click', (event) => {
      const deal = event.target.closest('.cm-builder__deal');
      if (!deal || deal.classList.contains('is-disabled') || deal.getAttribute('aria-disabled') === 'true') {
        return;
      }
      event.preventDefault();
      event.stopPropagation();
      try {
        const url = new URL(deal.getAttribute('href') || deal.href, window.location.origin);
        window.history.pushState({}, '', `${url.pathname}${url.search}`);
      } catch {
        /* ignore */
      }
      paint(cart);
      notifyPack();
    });

    document.addEventListener('cart:update', (event) => {
      const next = event.detail?.resource;
      if (next?.items) {
        paint(next);
        return;
      }
      queueRefresh();
    });

    document.addEventListener(
      'click',
      (event) => {
        if (
          !event.target.closest(
            'product-card [name="add"], product-card .add-to-cart-button, product-card button[type="submit"]'
          )
        ) {
          return;
        }
        queueRefresh(900);
      },
      true
    );

    window.addEventListener('popstate', () => {
      paint(cart);
      notifyPack();
    });

    refresh().then(notifyPack);
  };

  const start = () => {
    document.querySelectorAll('[data-cm-builder]').forEach(bind);
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, { once: true });
  else start();
  document.addEventListener('shopify:section:load', start);
})();
